import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import classNames from 'classnames';
import pluralize from 'pluralize';

import PhotoSelect from './components/PhotoSelect';
import Button from '../../../../../../../../../../../components/global/Button';
import DismissButton from '../../../../../../../../../../../components/global/DismissButton';
import { createPhotoSelectionCandidatePhotoGroups } from './helpers';
import CardElementPhotoSelectionControlGridSelect, {
  GRID_TYPES,
} from '../../../../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl/CardElementPhotoSelectionControlGridSelect';
import getAlbumMembershipGroupForPhotoSelection from '../../../../../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroupForPhotoSelection';
import getAlbumMembershipGroupIdentityForPhotoSelection from '../../../../../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroupIdentityForPhotoSelection';
import { AlbumForWebGalleriesPhotos } from '../../../../../../../../../../web-gallery/graphql/queries/album';

const propTypes = {
  defaultPhotoGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      total: PropTypes.number,
      photos: PropTypes.array,
      loadMore: PropTypes.func,
    }),
  ),
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  maxPhotos: PropTypes.number.isRequired,
  currentSelectedPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const PhotoSelectionOverlay = ({
  defaultPhotoGroups = [],
  onClose,
  onComplete,
  maxPhotos,
  currentSelectedPhotos,
  watermarkSettings,
}) => {
  const photoGroups = [...defaultPhotoGroups];
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState(
    currentSelectedPhotos ?? [],
  );
  const [activePhotoGroupIndex, setActivePhotoGroupIndex] = useState(0);
  const [gridType, setGridType] = useState(GRID_TYPES.SINGLE);
  const activePhotoGroup = photoGroups[activePhotoGroupIndex] || {};
  const { photos = [], loadMore, total = 0 } = activePhotoGroup;

  const onDone = () => {
    onComplete({
      photos: selectedPhotos,
    });
    onClose();
  };

  const navClasses = classNames({
    'photo-selection-overlay-nav': true,
    'd-none': (photoGroups?.length ?? 0) < 2,
  });

  const photoListClasses = classNames({
    'card-photo-selection-control-photo-list': true,
    'single-column': gridType === GRID_TYPES.SINGLE,
    'double-columns': gridType === GRID_TYPES.DOUBLE,
  });

  return (
    <div className="card-element-photo-selection-control-content">
      <div className="card-element-control-header">
        <span className="card-element-control-header-title">
          Select up to {pluralize('photo', maxPhotos, true)}
        </span>
        <DismissButton onClick={onClose} />
      </div>
      <nav className={navClasses}>
        {photoGroups.map((photoGroup, index) => (
          <button
            key={photoGroup.id}
            className={classNames({
              'photo-selection-overlay-nav-item': true,
              active: index === activePhotoGroupIndex,
            })}
            onClick={() => setActivePhotoGroupIndex(index)}
          >
            {photoGroup.label}
          </button>
        ))}
      </nav>
      {activePhotoGroup?.id !== 'custom-upload' && (
        <Fragment>
          <CardElementPhotoSelectionControlGridSelect
            onGridTypeChange={newGridType => setGridType(newGridType)}
          />
          <div className="card-photo-selection-control-scroll">
            <div className={photoListClasses} key={activePhotoGroup.id}>
              {photos.map(record => {
                const photo = record.photo || record;
                const isEntitled =
                  typeof photo?.is_entitled_matched_photo === 'undefined' ||
                  photo?.is_entitled_matched_photo;
                return (
                  <PhotoSelect
                    key={record?.id ?? photo.id}
                    photo={{
                      ...photo,
                      faceblocked_thumbnail_urls:
                        record?.faceblocked_thumbnail_urls,
                    }}
                    onSelect={() => {
                      if (isEntitled) {
                        return;
                      }

                      // Allow switching between photos when only 1 is selectable.
                      if (maxPhotos === 1) {
                        setSelectedPhotos([photo]);
                        return;
                      }

                      if (
                        selectedPhotos.length === maxPhotos &&
                        !selectedPhotos.find(p => p.id === photo.id)
                      ) {
                        return;
                      }

                      setSelectedPhotos(
                        selectedPhotos.find(p => p.id === photo.id)
                          ? selectedPhotos.filter(p => p.id !== photo.id)
                          : [...selectedPhotos, photo],
                      );
                    }}
                    selected={selectedPhotos.find(p => p.id === photo.id)}
                    isEntitled={isEntitled}
                    isSingleGrid={gridType === GRID_TYPES.SINGLE}
                    watermarkSettings={watermarkSettings}
                  />
                );
              })}
            </div>
          </div>
        </Fragment>
      )}
      {loadMore && photos.length < total && (
        <div className="card-photo-selection-control-photo-list-actions">
          <Button
            btnStyle="link"
            className="mb-2"
            onClick={async () => {
              setLoadingMore(true);
              await loadMore();
              setLoadingMore(false);
            }}
            btnBlock
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}

      <div className="card-element-control-footer">
        <Button
          btnBlock
          onClick={onDone}
          disabled={selectedPhotos.length === 0}
        >
          {selectedPhotos.length === 0
            ? `Select up to ${pluralize('photo', maxPhotos, true)}`
            : `Continue with ${pluralize(
                'photo',
                selectedPhotos.length,
                true,
              )}`}
        </Button>
      </div>
    </div>
  );
};

PhotoSelectionOverlay.propTypes = propTypes;

const withCandidatePhotos = graphql(
  getAlbumMembershipGroupIdentityForPhotoSelection,
  {
    options: ({ albumMembershipGroupId, identityId }) => ({
      variables: {
        album_membership_group_id: albumMembershipGroupId,
        identity_id: identityId,
      },
    }),
    props: ({
      data: { albumMembershipGroup, fetchMore, loading } = {},
      ownProps: {
        mappings = [
          {
            id: 'matches',
            label: 'Matches',
            path: 'identities[0].matched_photos_extended',
          },
          {
            id: 'allPhotos',
            label: 'All Photos',
            path: 'all_photos',
          },
        ],
      },
    }) => {
      return {
        loading,
        defaultPhotoGroups: createPhotoSelectionCandidatePhotoGroups({
          source: albumMembershipGroup,
          mappings,
          fetchMore,
        }),
      };
    },
    skip: ({ albumMembershipGroupId, identityId }) =>
      !albumMembershipGroupId || !identityId,
  },
);

const withCandidateIdentityPhotos = graphql(
  getAlbumMembershipGroupForPhotoSelection,
  {
    options: ({ albumMembershipGroupId }) => ({
      variables: {
        album_membership_group_id: albumMembershipGroupId,
      },
    }),
    props: ({
      data: { albumMembershipGroup, fetchMore, loading } = {},
      ownProps: {
        mappings = [
          {
            id: 'matches',
            label: 'Matches',
            path: 'matched_photos_extended',
          },
          {
            id: 'allPhotos',
            label: 'All Photos',
            path: 'all_photos',
          },
        ],
      },
    }) => {
      return {
        loading,
        defaultPhotoGroups: createPhotoSelectionCandidatePhotoGroups({
          source: albumMembershipGroup,
          mappings,
          fetchMore,
        }),
      };
    },
    skip: ({ albumMembershipGroupId, identityId }) =>
      !albumMembershipGroupId || identityId,
  },
);

const withCandidateAlbumPhotos = graphql(AlbumForWebGalleriesPhotos, {
  options: ({ albumId }) => ({
    variables: {
      id: albumId,
      limit: 40,
      offset: 0,
      use_all_photos_mode: true,
    },
  }),
  props: ({
    data: { album, fetchMore, loading } = {},
    ownProps: {
      mappings = [
        {
          id: 'allPhotos',
          label: 'All Photos',
          path: 'photos_ungrouped',
          offsetVariable: 'offset',
        },
      ],
    },
  }) => {
    return {
      loading,
      defaultPhotoGroups: createPhotoSelectionCandidatePhotoGroups({
        source: album,
        mappings,
        fetchMore,
      }),
    };
  },
  skip: ({ albumMembershipGroupId, albumId }) =>
    albumMembershipGroupId || !albumId,
});

export default compose(
  withCandidatePhotos,
  withCandidateIdentityPhotos,
  withCandidateAlbumPhotos,
)(PhotoSelectionOverlay);
